/**
 * Habit logging derivation: per-day completion/skip/clean/lapse status, and
 * the weekly counts that feed `DueHabitView.weekProgress`/`.allowance`
 * (domain-api.ts). Mirrors `objectives/state.ts`'s
 * `computeActionCompletionStatus` shape -- a pure reduction over
 * `excludingReverted(events)`.
 *
 * End-of-day clean resolution (phase-2b-habits.md §7 item 3, HB-7): the
 * ratified rule is "untouched days quietly resolve clean at end of day" for
 * avoid habits. RESOLUTION (dev-core-domain, this build): this needs NO
 * distinct scheduled event or backfill job. `DueHabitView.allowance` /
 * `WeekOutcome` are both computed purely from `habit.lapse_logged` events
 * inside the Mon-Sun window -- an avoid habit with zero lapses this week
 * already reads as "0 of N used", which IS "resolved clean" under the
 * existing allowance math with no special-casing. The PRD's "explicit tap
 * pays XP immediately, untouched days pay nothing retroactively" rule (PRD
 * v0.8 D8) is likewise already satisfied for free: XP only derives from
 * `habit.clean_logged` events (xp.ts), so a day that produces zero events
 * simply produces zero XP, never a negative or a missing "should have paid"
 * gap. If a future feature needs to know "was day X clean" as a first-class
 * fact (e.g. a per-day clean-streak badge, distinct from the weekly
 * allowance), THAT would need a new derivation (or event) at that time --
 * not assumed here, since nothing in this phase's scope (HB-7/AC-B11/AC-B12,
 * the weekly allowance and the weekly bonus which stays dormant this round
 * per phase-2b-habits.md §5) needs day-level clean tracking, only week-level
 * allowance counting, which the lapse-count math already provides.
 */

import type {
  CompassEvent,
  EventId,
  HabitCompletedEvent,
  HabitId,
  HabitSkippedEvent,
  WeekOutcome,
  WeekStart,
} from '../../contracts';
import { eventsOfTypes, excludingReverted } from '../event-log/event-log';
import { weeklyPlanMet } from './state';
import { isInWeek } from './week';

export type BuildLogStatus =
  | { readonly kind: 'done'; readonly entryEventId: EventId }
  | { readonly kind: 'skipped'; readonly entryEventId: EventId }
  | { readonly kind: 'pending' };

/**
 * Per-(habitId, date) status for build habits, keyed by `${habitId}|${date}`.
 * "Last (canonical-order) event wins" if both a completion and a skip exist
 * for the same day (mis-tap correction without needing a revert first) --
 * mirrors `computeActionCompletionStatus`'s "duplicates collapse, one entry
 * wins" pattern, generalized to two competing event types instead of one.
 */
export function computeBuildLogStatuses(
  events: readonly CompassEvent[],
): ReadonlyMap<string, BuildLogStatus> {
  const active = excludingReverted(events);
  const relevant = eventsOfTypes(active, ['habit.completed', 'habit.skipped']);

  const byKey = new Map<string, BuildLogStatus>();
  for (const event of relevant) {
    const key = logKey(event.habitId, event.occurredOn);
    byKey.set(key, {
      kind: event.type === 'habit.completed' ? 'done' : 'skipped',
      entryEventId: event.eventId,
    });
  }
  return byKey;
}

export function buildStatusFor(
  statuses: ReadonlyMap<string, BuildLogStatus>,
  habitId: HabitId,
  date: string,
): BuildLogStatus {
  return statuses.get(logKey(habitId, date)) ?? { kind: 'pending' };
}

/** Count of `habit.completed` events (active only) for one habit inside one Mon-Sun week. */
export function countBuildCompletionsInWeek(
  events: readonly CompassEvent[],
  habitId: HabitId,
  weekStart: WeekStart,
): number {
  const active = excludingReverted(events);
  return eventsOfTypes(active, ['habit.completed']).filter(
    (e) => e.habitId === habitId && isInWeek(e.occurredOn, weekStart),
  ).length;
}

export type AvoidLogStatus =
  | { readonly kind: 'clean'; readonly entryEventId: EventId }
  | { readonly kind: 'lapsed'; readonly entryEventId: EventId; readonly lapsesToday: number }
  | { readonly kind: 'pending' };

/**
 * Per-(habitId, date) status for avoid habits. Unlike build habits, MULTIPLE
 * lapses can be logged the same day (events.ts: "Multiple per day allowed;
 * each consumes one unit of the weekly allowance") -- `lapsesToday` counts
 * all active lapse events for that day; `entryEventId` for a lapsed day is
 * the MOST RECENT lapse event (canonical order), matching the convention
 * that undo targets "the last thing that happened" first.
 *
 * A `habit.clean_logged` and one or more `habit.lapse_logged` events for the
 * same day is an edge the UI is not expected to reach in normal use (the row
 * shows one pair of controls, tapping either logs that state) but the
 * derivation must still resolve deterministically: last event in canonical
 * order wins, exactly the build-habit "last wins" rule above.
 */
export function computeAvoidLogStatuses(
  events: readonly CompassEvent[],
): ReadonlyMap<string, AvoidLogStatus> {
  const active = excludingReverted(events);
  const relevant = eventsOfTypes(active, ['habit.clean_logged', 'habit.lapse_logged']);

  // Group by key in canonical order to compute lapsesToday and last-wins.
  const lapsesByKey = new Map<string, EventId[]>();
  const cleanByKey = new Map<string, EventId>();

  for (const event of relevant) {
    const key = logKey(event.habitId, event.occurredOn);
    if (event.type === 'habit.lapse_logged') {
      const list = lapsesByKey.get(key) ?? [];
      list.push(event.eventId);
      lapsesByKey.set(key, list);
    } else {
      cleanByKey.set(key, event.eventId);
    }
  }

  // Determine, per key, whether clean or lapse happened LAST (canonical
  // order) -- relevant is already in canonical order (eventsOfTypes
  // preserves input order, and `events` is assumed canonical per the event
  // log module's contract), so the last occurrence in the filtered list of
  // EITHER type is the winner.
  const lastEventKindByKey = new Map<string, 'clean' | 'lapse'>();
  for (const event of relevant) {
    const key = logKey(event.habitId, event.occurredOn);
    lastEventKindByKey.set(key, event.type === 'habit.clean_logged' ? 'clean' : 'lapse');
  }

  const byKey = new Map<string, AvoidLogStatus>();
  const allKeys = new Set([...lapsesByKey.keys(), ...cleanByKey.keys()]);
  for (const key of allKeys) {
    const lastKind = lastEventKindByKey.get(key);
    if (lastKind === 'lapse') {
      const lapseIds = lapsesByKey.get(key) ?? [];
      const lastLapseId = lapseIds[lapseIds.length - 1];
      if (lastLapseId) {
        byKey.set(key, {
          kind: 'lapsed',
          entryEventId: lastLapseId,
          lapsesToday: lapseIds.length,
        });
      }
    } else if (lastKind === 'clean') {
      const cleanId = cleanByKey.get(key);
      if (cleanId) {
        byKey.set(key, { kind: 'clean', entryEventId: cleanId });
      }
    }
  }

  return byKey;
}

export function avoidStatusFor(
  statuses: ReadonlyMap<string, AvoidLogStatus>,
  habitId: HabitId,
  date: string,
): AvoidLogStatus {
  return statuses.get(logKey(habitId, date)) ?? { kind: 'pending' };
}

/** Count of `habit.lapse_logged` events (active only) for one habit inside one Mon-Sun week. */
export function countLapsesInWeek(
  events: readonly CompassEvent[],
  habitId: HabitId,
  weekStart: WeekStart,
): number {
  const active = excludingReverted(events);
  return eventsOfTypes(active, ['habit.lapse_logged']).filter(
    (e) => e.habitId === habitId && isInWeek(e.occurredOn, weekStart),
  ).length;
}

/**
 * One habit's planned-vs-actual outcome for one Mon-Sun week (`WeekOutcome`,
 * domain-api.ts) -- the single shared computation behind both the weekly
 * review's per-habit report (`review/weekly-review.ts`) and
 * `ObjectiveView.linkedHabits`' current-week adherence
 * (`objectives/projection.ts`, phase-3 spec §2 HL-2). Moved here (rather than
 * staying private to `weekly-review.ts`, where it originated) so a second,
 * independent caller does not have to either duplicate it or import the
 * review module -- `logging.ts` already owns `countBuildCompletionsInWeek`/
 * `countLapsesInWeek`, the two counts this composes, making it the natural
 * shared home. Behavior is unchanged; `weekly-review.ts` now imports this
 * instead of defining its own copy (re-exported there as `computeWeekOutcome`
 * for existing external callers/tests, unchanged name).
 */
export function computeWeekOutcome(
  events: readonly CompassEvent[],
  habitId: HabitId,
  weekStart: WeekStart,
  kind: 'build' | 'avoid',
  planned: number,
): WeekOutcome {
  const actual =
    kind === 'build'
      ? countBuildCompletionsInWeek(events, habitId, weekStart)
      : countLapsesInWeek(events, habitId, weekStart);
  const met = weeklyPlanMet(kind, actual, planned);
  return { weekStart, planned, actual, met };
}

function logKey(habitId: HabitId, date: string): string {
  return `${habitId}|${date}`;
}

/** Re-exported for callers needing the raw event types without a second import line. */
export type { HabitCompletedEvent, HabitSkippedEvent };
